import { esc } from '../escape.js';

// Navbar notification bell: draft Purchase Orders / pending shift corrections /
// pending leave requests / leave starting soon (7-day + 1-day reminder) /
// shift swaps + pickups waiting for a manager (2026-10-17), all
// gated by the controller on the signed-in account's actual capabilities (not
// the switchable cashier - see controller.js _refreshNotifications). This view
// only renders what it's handed; it never checks permissions itself.

const fmtRelDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fmtPoNumber = (n) => `PO-${String(n ?? 0).padStart(3, '0')}`;

const fmtStartsIn = (dateStr) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(dateStr + 'T00:00:00') - today) / 86400000);
  return days === 1 ? 'Starts tomorrow' : `Starts in ${days} days`;
};

class NotificationView {
  _control  = document.getElementById('notifControl');
  _toggle   = document.getElementById('notifToggle');
  _dropdown = document.getElementById('notifDropdown');
  _list     = document.getElementById('notifList');
  _badge    = document.getElementById('notifBadge');

  _categoryClickHandler = null;
  _itemClickHandler     = null;

  _addHandlerCategoryClick(handler) { this._categoryClickHandler = handler; }
  _addHandlerItemClick(handler)     { this._itemClickHandler = handler; }

  reveal() {
    this._control?.classList.remove('hidden');
  }

  // { pos, corrections, leave, leaveUpcoming, shiftRequests }, { canPOs, canPay, canSchedule, leaveUpcomingUnseen }
  render({ pos = [], corrections = [], leave = [], leaveUpcoming = [], shiftRequests = [] } = {}, { canPOs = false, canPay = false, canSchedule = false, leaveUpcomingUnseen = leaveUpcoming.length } = {}) {
    if (!this._list || !this._badge) return;

    const sections = [];
    if (canPOs) sections.push(this._section('po', '📝', 'Purchase orders', pos, (po) =>
      `<span class="notif-item-title">${esc(fmtPoNumber(po.poNumber))}</span><span class="notif-item-sub">${esc(po.supplierName || 'No supplier')}</span>`));
    if (canPay) sections.push(this._section('corrections', '⏱️', 'Shift corrections', corrections, (r) =>
      `<span class="notif-item-title">${esc(r.staffName || 'Staff')}</span><span class="notif-item-sub">${r.shiftId ? 'Correction' : 'Missed shift'} · ${esc(fmtRelDate(r.createdAt))}</span>`));
    if (canPay) sections.push(this._section('leave', '📅', 'Leave requests', leave, (r) =>
      `<span class="notif-item-title">${esc(r.staffName || 'Staff')}</span><span class="notif-item-sub">${r.type === 'sick' ? '🤒 Sick' : '✈️ Vacation'} · ${esc(fmtRelDate(r.createdAt))}</span>`));
    // badgeCount is the UNSEEN count, not rows.length - unlike the other three
    // categories, a reminder here never resolves on its own (no approve/reject),
    // so it has to be dismissed by opening the bell instead, or it would just
    // sit in the badge for its whole 7-day/1-day window. Rows still list every
    // active reminder (seen or not) - only the count badge reacts to "seen".
    if (canPay) sections.push(this._section('leaveUpcoming', '⏳', 'Upcoming leave', leaveUpcoming, (r) =>
      `<span class="notif-item-title">${esc(r.staffName || 'Staff')}</span><span class="notif-item-sub">${esc(fmtStartsIn(r.startDate))}</span>`, 'Nothing coming up', leaveUpcomingUnseen));

    // Swaps + open-shift pickups waiting for a manager (shift schedule phase 3).
    if (canSchedule) sections.push(this._section('shiftRequests', '🔁', 'Shift requests', shiftRequests, (r) =>
      `<span class="notif-item-title">${esc(r.requesterName || 'Staff')}</span><span class="notif-item-sub">${r.kind === 'pickup' ? 'Wants an open shift' : r.targetShiftId ? `Trade with ${esc(r.targetName || 'a teammate')}` : `Cover by ${esc(r.targetName || 'a teammate')}`} · ${esc(fmtRelDate(r.createdAt))}</span>`));

    const total = sections.reduce((s, sec) => s + sec.count, 0);
    this._list.innerHTML = sections.length
      ? sections.map((sec) => sec.html).join('')
      : `<div class="notif-empty">You're all caught up 🎉</div>`;

    if (total > 0) {
      this._badge.textContent = total > 99 ? '99+' : String(total);
      this._badge.classList.remove('hidden');
    } else {
      this._badge.classList.add('hidden');
    }
  }

  _section(cat, icon, label, rows, rowHtml, emptyText = 'None pending', badgeCount = rows.length) {
    const html = `
      <div class="notif-section">
        <button class="notif-section-head" data-cat="${cat}" type="button">
          <span class="notif-section-icon" aria-hidden="true">${icon}</span>
          <span class="notif-section-label">${label}</span>
          <span class="notif-section-count">${badgeCount}</span>
        </button>
        ${rows.length ? `<div class="notif-section-items">
          ${rows.slice(0, 5).map((r) => `
            <button class="notif-item" data-cat="${cat}" data-id="${esc(r.id)}" type="button">
              ${rowHtml(r)}
            </button>`).join('')}
          ${rows.length > 5 ? `<button class="notif-item notif-item--more" data-cat="${cat}" type="button">+${rows.length - 5} more</button>` : ''}
        </div>` : `<p class="notif-section-empty">${esc(emptyText)}</p>`}
      </div>`;
    return { count: badgeCount, html };
  }

  _addHandlerDropdownClicks() {
    this._list?.addEventListener('click', (e) => {
      const itemBtn = e.target.closest('.notif-item');
      const headBtn = e.target.closest('.notif-section-head');
      if (itemBtn) {
        const { cat, id } = itemBtn.dataset;
        if (id) this._itemClickHandler?.(cat, id);
        else this._categoryClickHandler?.(cat); // "+N more" row
      } else if (headBtn) {
        this._categoryClickHandler?.(headBtn.dataset.cat);
      }
    });
  }
}

const _view = new NotificationView();
_view._addHandlerDropdownClicks();
export default _view;
